/**
 * Title Insurance Premium Calculation Engine
 * Pure functions — no side effects, easily testable.
 */
import { PA, NJ, NY, FL } from './rates';

// Compute premium from a tiered rate table
function calcTiered(amount, tiers) {
  if (amount <= 0) return 0;
  let premium = 0;
  let remaining = amount;
  let prevCap = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierSize = tier.upTo - prevCap;
    const applicable = Math.min(remaining, tierSize);

    if (tier.base !== undefined && prevCap === 0) {
      premium += tier.base;
    } else {
      premium += (applicable / 1000) * tier.perThousand;
    }
    remaining -= applicable;
    prevCap = tier.upTo;
  }
  return Math.round(premium * 100) / 100;
}

// --- PENNSYLVANIA ---
export function calculatePA(inputs) {
  const { transactionType, purchasePrice, loanAmount } = inputs;
  const isPurchase = transactionType === 'purchase';
  const coverageAmount = isPurchase ? (purchasePrice || 0) : (loanAmount || 0);
  const tiers = isPurchase ? PA.saleTiers : PA.nonSaleTiers;
  const premium = calcTiered(coverageAmount, tiers);

  const notes = [];
  if (isPurchase) {
    notes.push("Pennsylvania's all-inclusive premium covers both the owner's and lender's title policies at no additional charge.");
  } else {
    notes.push("Non-Sale Rate applies to refinance transactions.");
    notes.push("Reissue credits (10-40% off) may apply if a prior policy was issued within 10 years. Contact us for details.");
  }

  return {
    state: 'PA',
    transactionType,
    coverageAmount,
    ownerPremium: isPurchase ? premium : null,
    lenderPremium: isPurchase ? 0 : premium,
    totalPremium: premium,
    simultaneousNote: isPurchase ? "Lender's policy included at no additional charge" : null,
    notes,
  };
}

// --- NEW JERSEY ---
export function calculateNJ(inputs) {
  const { transactionType, purchasePrice, loanAmount } = inputs;
  const isPurchase = transactionType === 'purchase';
  const se = NJ.searchAndExamFee;

  if (isPurchase) {
    const ownerAmount = purchasePrice || 0;
    const loanAmt = loanAmount || 0;
    const ownerUnderwriting = Math.max(calcTiered(ownerAmount, NJ.standardTiers), NJ.minimumPremium);
    const ownerTotal = ownerUnderwriting + se;

    let lenderTotal = 0;
    let lenderNote = null;
    if (loanAmt > 0) {
      if (loanAmt <= ownerAmount) {
        lenderTotal = NJ.simultaneousLoanFlat + se;
        lenderNote = `Simultaneous issue: $${NJ.simultaneousLoanFlat.toFixed(2)} flat + $${se.toFixed(2)} S&E`;
      } else {
        const excessUnderwriting = calcTiered(loanAmt - ownerAmount, NJ.standardTiers);
        lenderTotal = NJ.simultaneousLoanFlat + excessUnderwriting + se;
        lenderNote = `Simultaneous issue: $${NJ.simultaneousLoanFlat.toFixed(2)} flat + excess coverage at standard rates + S&E`;
      }
    }

    return {
      state: 'NJ',
      transactionType,
      coverageAmount: ownerAmount,
      ownerPremium: ownerTotal,
      lenderPremium: lenderTotal,
      totalPremium: ownerTotal + lenderTotal,
      simultaneousNote: lenderNote,
      notes: [
        `Includes $${se.toFixed(2)} Search & Examination fee per policy.`,
        `Minimum underwriting charge: $${NJ.minimumPremium.toFixed(2)}.`,
      ],
    };
  }

  // Refinance
  const loanAmt = loanAmount || 0;
  const lenderUnderwriting = Math.max(calcTiered(loanAmt, NJ.refinanceTiers), NJ.minimumPremium);
  const lenderTotal = lenderUnderwriting + se;

  return {
    state: 'NJ',
    transactionType,
    coverageAmount: loanAmt,
    ownerPremium: null,
    lenderPremium: lenderTotal,
    totalPremium: lenderTotal,
    notes: [
      "Refinance rate applies to same-borrower, same-property refinances.",
      `Includes $${se.toFixed(2)} Search & Examination fee.`,
    ],
  };
}

// --- NEW YORK ---
export function calculateNY(inputs) {
  const { transactionType, purchasePrice, loanAmount, zone } = inputs;
  const isPurchase = transactionType === 'purchase';
  const zoneData = zone === 'zone1' ? NY.zones.zone1 : NY.zones.zone2;
  const zoneLabel = zoneData.label;

  if (isPurchase) {
    const ownerAmount = purchasePrice || 0;
    const loanAmt = loanAmount || 0;
    const usePurchaseRates = ownerAmount <= 1000000;
    const ownerTiers = usePurchaseRates ? zoneData.purchaseOwner : zoneData.regularOwner;
    const loanTiers = usePurchaseRates ? zoneData.purchaseLoan : zoneData.regularLoan;

    const ownerPremium = calcTiered(ownerAmount, ownerTiers);

    let lenderPremium = 0;
    let simNote = null;
    if (loanAmt > 0) {
      const fullLoanPremium = calcTiered(Math.min(loanAmt, ownerAmount), loanTiers);
      const simPortion = fullLoanPremium * NY.simultaneousLoanPercent;
      if (loanAmt > ownerAmount) {
        const excessPremium = calcTiered(loanAmt, loanTiers) - calcTiered(ownerAmount, loanTiers);
        lenderPremium = simPortion + excessPremium;
        simNote = "Simultaneous issue: 30% of loan rate (up to owner's amount) + full rate on excess";
      } else {
        lenderPremium = simPortion;
        simNote = "Simultaneous issue: 30% of the applicable loan rate";
      }
      lenderPremium = Math.round(lenderPremium * 100) / 100;
    }

    const notes = [`Rate zone: ${zoneLabel}.`];
    if (usePurchaseRates) {
      notes.push("Discounted purchase rates applied (coverage up to $1,000,000).");
    } else {
      notes.push("Regular rates applied (coverage exceeds $1,000,000).");
    }

    return {
      state: 'NY',
      transactionType,
      coverageAmount: ownerAmount,
      ownerPremium,
      lenderPremium,
      totalPremium: Math.round((ownerPremium + lenderPremium) * 100) / 100,
      simultaneousNote: simNote,
      notes,
    };
  }

  // Refinance
  const loanAmt = loanAmount || 0;
  const loanTiers = zoneData.regularLoan;
  const fullPremium = calcTiered(loanAmt, loanTiers);
  const discountPct = loanAmt <= NY.refinanceThreshold
    ? NY.refinanceDiscountAtOrBelow
    : NY.refinanceDiscountAbove;
  const lenderPremium = Math.round(fullPremium * discountPct * 100) / 100;

  return {
    state: 'NY',
    transactionType,
    coverageAmount: loanAmt,
    ownerPremium: null,
    lenderPremium,
    totalPremium: lenderPremium,
    notes: [
      `Rate zone: ${zoneLabel}.`,
      `Refinance discount: ${Math.round(discountPct * 100)}% of the regular loan rate.`,
      "Applies to refinances within 10 years on the same or similar property.",
    ],
  };
}

// --- FLORIDA ---
export function calculateFL(inputs) {
  const { transactionType, purchasePrice, loanAmount, priorLoanYears } = inputs;
  const isPurchase = transactionType === 'purchase';

  if (isPurchase) {
    const ownerAmount = purchasePrice || 0;
    const loanAmt = loanAmount || 0;
    const ownerPremium = Math.max(calcTiered(ownerAmount, FL.originalTiers), FL.minimumPremium);

    let lenderPremium = 0;
    let simNote = null;
    if (loanAmt > 0) {
      if (loanAmt <= ownerAmount) {
        lenderPremium = FL.simultaneousLoanFlat;
        simNote = `Simultaneous issue: $${FL.simultaneousLoanFlat.toFixed(2)} flat`;
      } else {
        const excessPremium = calcTiered(loanAmt, FL.originalTiers) - calcTiered(ownerAmount, FL.originalTiers);
        lenderPremium = FL.simultaneousLoanFlat + excessPremium;
        simNote = `Simultaneous issue: $${FL.simultaneousLoanFlat.toFixed(2)} flat + full rate on excess over owner's coverage`;
      }
    }

    return {
      state: 'FL',
      transactionType,
      coverageAmount: ownerAmount,
      ownerPremium,
      lenderPremium,
      totalPremium: Math.round((ownerPremium + lenderPremium) * 100) / 100,
      simultaneousNote: simNote,
      notes: [
        "Florida promulgated rates apply statewide.",
        `Minimum premium: $${FL.minimumPremium.toFixed(2)}.`,
      ],
    };
  }

  // Refinance (substitution loan)
  const loanAmt = loanAmount || 0;
  const years = priorLoanYears || 0;
  const bracket = FL.refinanceDiscounts.find(d => years <= d.maxYears) || FL.refinanceDiscounts[FL.refinanceDiscounts.length - 1];
  const fullPremium = calcTiered(loanAmt, FL.originalTiers);
  const lenderPremium = Math.max(Math.round(fullPremium * bracket.percent * 100) / 100, FL.minimumPremium);

  return {
    state: 'FL',
    transactionType,
    coverageAmount: loanAmt,
    ownerPremium: null,
    lenderPremium,
    totalPremium: lenderPremium,
    notes: [
      `Substitution loan rate: ${Math.round(bracket.percent * 100)}% of original rates (prior loan: ${bracket.label}).`,
      `Minimum premium: $${FL.minimumPremium.toFixed(2)}.`,
    ],
  };
}

export function calculate(state, inputs) {
  switch (state) {
    case 'PA': return calculatePA(inputs);
    case 'NJ': return calculateNJ(inputs);
    case 'NY': return calculateNY(inputs);
    case 'FL': return calculateFL(inputs);
    default: throw new Error(`Unsupported state: ${state}`);
  }
}
